import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, firestore, googleProvider, storage } from './firebase/config';
import { createUserWithEmailAndPassword, signInWithPopup, updateProfile } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const BusinessUserSignUp = () => {
  const navigate = useNavigate();
  
  // Basic auth info
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  
  // Business info
  const [businessInfo, setBusinessInfo] = useState({
    name: '',
    address: '',
    phone: '',
    website: '',
    industry: '',
    description: ''
  });
  
  // UI state
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState('');

  // Handle account creation (step 1)
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile display name
      await updateProfile(userCredential.user, {
        displayName: name
      });
      
      // Store basic user data in Firestore
      await setDoc(doc(firestore, 'users', userCredential.user.uid), {
        firstName: name.split(' ')[0] || '',
        lastName: name.split(' ').slice(1).join(' ') || '',
        email: email,
        role: 'business_user',
        createdAt: new Date()
      });
      
      console.log('Business user account created:', userCredential.user);
      setAccountCreated(true);
      // Proceed to next step
      setCurrentStep(2);
    } catch (error) {
      console.error('Error signing up:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle business information submission (step 2)
  const handleBusinessInfoSubmit = async () => {
    if (!accountCreated) return;
    
    setLoading(true);
    try {
      const businessData = {
        ...businessInfo,
        updatedAt: new Date()
      };
      
      // Upload business logo if provided
      if (profileImage) {
        const storageRef = ref(storage, `businessLogo/${auth.currentUser.uid}`);
        await uploadBytes(storageRef, profileImage);
        const downloadURL = await getDownloadURL(storageRef);
        businessData.logoURL = downloadURL;
        
        // Update user profile photo URL
        await updateProfile(auth.currentUser, {
          photoURL: downloadURL
        });
      }
      
      // Update business info in Firestore
      await setDoc(doc(firestore, 'businessProfiles', auth.currentUser.uid), businessData, { merge: true });
      
      console.log('Business profile updated successfully');
      navigate('/business-dashboard');
    } catch (error) {
      console.error('Error updating business profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle file selection for profile image
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setProfileImage(e.target.files[0]);
      setProfileImageUrl(URL.createObjectURL(e.target.files[0]));
    }
  };
  
  // Handle form submission based on current step
  const handleSubmit = (e) => {
    e.preventDefault();
    
    switch (currentStep) {
      case 1:
        handleCreateAccount(e);
        break;
      case 2:
        handleBusinessInfoSubmit();
        break;
      default:
        break;
    }
  };
  
  // Handle Google sign up
  const handleGoogleSignUp = async () => {
    setError('');
    setLoading(true);
    
    try {
      // Sign in with Google
      const result = await signInWithPopup(auth, googleProvider);
      
      // Check if this is a new user
      const isNewUser = result._tokenResponse.isNewUser;
      
      if (isNewUser) {
        // Create user record in Firestore
        await setDoc(doc(firestore, 'users', result.user.uid), {
          firstName: result.user.displayName?.split(' ')[0] || '',
          lastName: result.user.displayName?.split(' ').slice(1).join(' ') || '',
          email: result.user.email,
          role: 'business_user',
          createdAt: new Date()
        });
        
        // Set initial state and move to profile completion
        setName(result.user.displayName || '');
        setEmail(result.user.email || '');
        setProfileImageUrl(result.user.photoURL || '');
        setAccountCreated(true);
        setCurrentStep(2);
      } else {
        // Existing user, redirect to dashboard
        navigate('/business-dashboard');
      }
    } catch (error) {
      console.error('Error with Google sign up:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Close the sign up page
  const handleClose = () => {
    navigate('/');
  };

  // Update business info field
  const handleBusinessInfoChange = (e) => {
    const { name, value } = e.target;
    setBusinessInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Render appropriate step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="name" className="block text-left text-gray-700">
                  Full Name<span className="text-orange-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="email" className="block text-left text-gray-700">
                  Email Address<span className="text-orange-500">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="password" className="block text-left text-gray-700">
                  Password<span className="text-orange-500">*</span>
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Choose a strong password"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
                <p className="text-xs text-gray-500">
                  Password must be at least 8 characters
                </p>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-md hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50 disabled:opacity-70"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
            
            <div className="relative flex items-center justify-center my-4">
              <div className="border-t border-gray-300 w-full"></div>
              <span className="bg-white px-3 text-sm text-gray-500 absolute">or</span>
            </div>
            
            <button
              type="button"
              onClick={handleGoogleSignUp}
              disabled={loading}
              className="w-full py-3 border border-gray-300 rounded-md flex items-center justify-center space-x-2 hover:bg-gray-50 transition-colors disabled:opacity-70"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>Continue with Google</span>
            </button>
          </>
        );
        
      case 2:
        return (
          <>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <label htmlFor="profileImage" className="block text-left text-gray-700">
                  Business Logo
                </label>
                <div className="flex items-center space-x-4">
                  <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                    {profileImageUrl ? (
                      <img src={profileImageUrl} alt="Business Logo" className="w-full h-full object-cover" />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    )}
                  </div>
                  <input
                    id="profileImage"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  <label htmlFor="profileImage" className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md cursor-pointer hover:bg-gray-300">
                    Upload Logo
                  </label>
                </div>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="businessName" className="block text-left text-gray-700">
                  Business Name<span className="text-orange-500">*</span>
                </label>
                <input
                  id="businessName"
                  name="name"
                  type="text"
                  value={businessInfo.name}
                  onChange={handleBusinessInfoChange}
                  placeholder="Your business name"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="industry" className="block text-left text-gray-700">
                  Industry<span className="text-orange-500">*</span>
                </label>
                <input
                  id="industry"
                  name="industry"
                  type="text"
                  value={businessInfo.industry}
                  onChange={handleBusinessInfoChange}
                  placeholder="e.g. Restaurant, Hospitality, Retail"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="address" className="block text-left text-gray-700">
                  Business Address
                </label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  value={businessInfo.address}
                  onChange={handleBusinessInfoChange}
                  placeholder="Street address, city, state, zip"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="phone" className="block text-left text-gray-700">
                  Phone Number
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={businessInfo.phone}
                  onChange={handleBusinessInfoChange}
                  placeholder="Business phone number"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="website" className="block text-left text-gray-700">
                  Website
                </label>
                <input
                  id="website"
                  name="website"
                  type="url"
                  value={businessInfo.website}
                  onChange={handleBusinessInfoChange}
                  placeholder="https://yourbusiness.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="description" className="block text-left text-gray-700">
                  Business Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={businessInfo.description}
                  onChange={handleBusinessInfoChange}
                  placeholder="Tell job seekers about your business..."
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-md hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50 disabled:opacity-70"
                >
                  {loading ? 'Completing Setup...' : 'Complete Setup'}
                </button>
              </div>
            </div>
          </>
        );
        
      default:
        return null;
    }
  };

  // Render step indicator for step 2
  const renderStepIndicator = () => {
    if (currentStep === 1) return null;
    
    return (
      <div className="mb-8">
        <div className="flex justify-between items-center">
          {[1, 2].map((step) => (
            <div key={step} className="flex flex-col items-center">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  currentStep === step 
                    ? 'bg-orange-500 text-white' 
                    : currentStep > step 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-200 text-gray-600'
                }`}
              >
                {currentStep > step ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  step
                )}
              </div>
              <span className="text-xs mt-1 text-gray-500">
                {step === 1 && 'Account'}
                {step === 2 && 'Business Info'}
              </span>
            </div>
          ))}
        </div>
        <div className="relative flex items-center justify-between mt-2">
          <div className="absolute left-0 right-0 h-1 bg-gray-200">
            <div 
              className="absolute h-1 bg-orange-500 transition-all"
              style={{ width: `${(currentStep - 1) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  // Get step title based on current step
  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return "Create Your Business Account";
      case 2:
        return "Complete Your Business Profile";
      default:
        return "";
    }
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen w-full overflow-hidden bg-gradient-to-br from-orange-50 to-orange-100">
      {/* Background pattern */}
      <div className="absolute inset-0 grid grid-cols-4 grid-rows-3 gap-4 z-0">
        {Array(12).fill().map((_, i) => (
          <div key={i} className="rounded-lg bg-gradient-to-br from-orange-200 to-orange-100 opacity-50"></div>
        ))}
      </div>
      
      {/* Main container */}
      <div className="relative z-10 flex flex-row w-full max-w-4xl bg-white rounded-lg shadow-xl overflow-hidden">
        {/* Form side */}
        <div className="w-full md:w-1/2 p-8 md:p-12">
          <div className="flex items-center mb-8">
            <img 
              src="/asset/Logo/hireeLogo.png" 
              alt="Hiree Logo" 
              className="h-10 w-auto"
            />
            <span className="ml-3 text-xl text-gray-600">Create business account</span>
          </div>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}
          
          {renderStepIndicator()}
          
          <h2 className="text-xl font-semibold text-gray-800 mb-4">{getStepTitle()}</h2>
          
          <form onSubmit={handleSubmit}>
            {renderStepContent()}
          </form>
          
          {currentStep === 1 && (
            <div className="mt-6 text-center text-sm">
              <span className="text-gray-600">Already have an account?</span>{' '}
              <Link to="/login" className="text-orange-500 hover:text-orange-600">
                Sign in
              </Link>
            </div>
          )}
        </div>
        
        {/* Video side - only shown on medium screens and larger */}
        <div className="hidden md:block md:w-1/2 relative">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 z-20 p-2 bg-white bg-opacity-70 rounded-full hover:bg-opacity-100"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <video autoPlay muted loop className="absolute inset-0 w-full h-full object-cover">
            <source src="/asset/SignInPage/video1.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        </div>
      </div>
    </div>
  );
};

export default BusinessUserSignUp;
